import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { AppButton } from '@/shared/components/ui/AppButton';
import { AppCard } from '@/shared/components/ui/AppCard';
import { useAuthStore } from '@/shared/stores/auth-store';
import { selectOrganization } from '../../api/auth-api';
import { setSessionToken } from '@/shared/services/session-token';
import styles from './OrganizationSelectPage.module.scss';

export function OrganizationSelectPage() {
  const navigate = useNavigate();
  const organizations = useAuthStore((state) => state.organizations);
  const setSession = useAuthStore((state) => state.setSession);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleSelect(organizationId: string) {
    setPendingId(organizationId);
    setError(null);

    try {
      const session = await selectOrganization(organizationId);
      setSessionToken(session.token);
      setSession({
        user: session.user,
        permissions: session.permissions,
        orgId: session.selectedOrganizationId,
        organizations: session.organizations,
      });
      await navigate({ to: '/promote' });
    } catch (selectionError) {
      const message =
        selectionError && typeof selectionError === 'object' && 'message' in selectionError
          ? String(selectionError.message)
          : 'Unable to switch organization.';
      setError(message);
    } finally {
      setPendingId(null);
    }
  }

  return (
    <div className={styles.page}>
      <AppCard className={styles.card}>
        <h1 className={styles.title}>Choose an organization</h1>
        <p className={styles.subtitle}>
          Select the workspace you want to manage before entering Promote.
        </p>

        <div className={styles.list}>
          {organizations.map((organization) => (
            <div key={organization.id} className={styles.organization}>
              <span>
                <strong>{organization.name}</strong>
                <small>{organization.email}</small>
              </span>
              <AppButton
                size="sm"
                onClick={() => handleSelect(organization.id)}
                loading={pendingId === organization.id}
                disabled={pendingId !== null}
              >
                Open
              </AppButton>
            </div>
          ))}
        </div>

        {error && <p className={styles.error}>{error}</p>}
      </AppCard>
    </div>
  );
}
